import { useCallback, useContext, useEffect, useState } from "react";
import { getApiUrl } from "../utils/api";
import { useLocation } from "react-router-dom";
import {
  FaBook,
  FaComments,
  FaFeatherAlt,
  FaUsers,
} from "react-icons/fa";
import GroupInviteSection from "./GroupInviteSection";
import SidebarFooter from "./SidebarFooter";
import { UserContext } from "../context/UserContext";

function getGroupIdFromPath(pathname) {
  const match = pathname.match(/^\/groups\/([^/]+)/);
  return match ? match[1] : null;
}

export default function GroupRightSidebar() {
  const { user } = useContext(UserContext);
  const location = useLocation();
  const groupId = getGroupIdFromPath(location.pathname);

  const [group, setGroup] = useState(null);

  const loadData = useCallback(async () => {
    if (!user || !groupId) {
      setGroup(null);
      return;
    }

    try {
      const groupRes = await fetch(getApiUrl(`/api/groups/${groupId}`), {
        credentials: "include",
      });

      if (groupRes.ok) {
        const groupData = await groupRes.json();
        setGroup(groupData);
      } else {
        setGroup(null);
      }
    } catch (err) {
      console.error("Failed to load group sidebar:", err);
      setGroup(null);
    }
  }, [user, groupId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const onRefresh = (e) => {
      if (!groupId) return;
      if (e.detail?.groupId && String(e.detail.groupId) !== String(groupId)) return;
      loadData();
    };
    const onDiscussionsChanged = (e) => {
      if (!groupId) return;
      if (e.detail?.groupId && String(e.detail.groupId) !== String(groupId)) return;
      if (typeof e.detail?.discussionCount === "number") {
        setGroup((prev) =>
          prev ? { ...prev, discussionCount: e.detail.discussionCount } : prev
        );
        return;
      }
      loadData();
    };

    window.addEventListener("group-invites-changed", onRefresh);
    window.addEventListener("groups-updated", onRefresh);
    window.addEventListener("group-new-post", onRefresh);
    window.addEventListener("group-discussions-changed", onDiscussionsChanged);
    return () => {
      window.removeEventListener("group-invites-changed", onRefresh);
      window.removeEventListener("groups-updated", onRefresh);
      window.removeEventListener("group-new-post", onRefresh);
      window.removeEventListener("group-discussions-changed", onDiscussionsChanged);
    };
  }, [groupId, loadData]);

  const showGroupPanels = groupId && user && group?.isMember;

  return (
    <aside className="app-right-sidebar group-rail" aria-label="Group sidebar">
      <div className="app-right-sidebar-scroll">
      {showGroupPanels && (
      <div className="app-right-sidebar-panel">
        <section className="app-right-sidebar-card group-rail__card">
          <h2 className="group-rail__title">About this group</h2>
          <p className="group-rail__text">
            {group.tagline ||
              group.description ||
              "A gathering place for poets to share and respond to each other's work."}
          </p>
          <div className="group-rail__tags">
            <span className="group-rail__tag">
              <FaFeatherAlt aria-hidden="true" />
              Poetry
            </span>
            <span className="group-rail__tag">
              <FaUsers aria-hidden="true" />
              All are welcome
            </span>
          </div>
        </section>

        <section className="app-right-sidebar-card group-rail__card">
          <h2 className="group-rail__title">Group stats</h2>
          <div className="group-rail__stats">
            <div className="group-rail__stat">
              <FaUsers className="group-rail__stat-icon" aria-hidden="true" />
              <span className="group-rail__stat-value">
                {group.memberCount ?? 0}
              </span>
              <span className="group-rail__stat-label">Members</span>
            </div>
            <div className="group-rail__stat">
              <FaBook className="group-rail__stat-icon" aria-hidden="true" />
              <span className="group-rail__stat-value">
                {group.poemCount ?? 0}
              </span>
              <span className="group-rail__stat-label">Poems</span>
            </div>
            <div className="group-rail__stat">
              <FaComments className="group-rail__stat-icon" aria-hidden="true" />
              <span className="group-rail__stat-value">
                {group.discussionCount ?? 0}
              </span>
              <span className="group-rail__stat-label">Discussions</span>
            </div>
          </div>
        </section>

        <GroupInviteSection
          groupId={groupId}
          groupName={group.name}
          instanceId="sidebar"
        />
      </div>
      )}
      <SidebarFooter />
      </div>
    </aside>
  );
}
